import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import LoginInfo from "../login/LoginInfo";
import LogoImage from "../../../images/dried-mango-logo.svg";
import Form from "react-bootstrap/Form";
import Button from "../../../components/shared/buttons/button";
import ViewPassword from "../../../images/eye-icon.svg";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useResetPasswordMutation } from "../authApi";
import HidePassword from "../../../images/eye-off.svg";
import { isStrongPassword, showToast } from "../../../helpers/Utils";
import LanguageToggleSwitch from "../../../components/shared/LanguageToggleSwitch/LanguageToggleSwitch";
import useTranslator from "../../../hooks/useTranslator";

const ResetPassword = () => {
  const { _t } = useTranslator();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  useEffect(() => {
    if (!token || !email) {
      showToast("Your reset link has expired or is invalid. Please request a new password reset.", "error");
      navigate("/login");
    }
  }, [token, email, navigate]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      showToast("Please enter and confirm your new password.", "error");
      return;
    }

    if (!isStrongPassword(password)) {
      showToast(
        "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character.",
        "error"
      );
      return;
    }

    if (password !== confirmPassword) {
      showToast("The passwords you entered don't match. Please check and try again.", "error");
      return;
    }

    try {
      const requestBody = {
        email: email,
        token: token,
        password: password,
        password_confirmation: confirmPassword,
      };

      await resetPassword(requestBody).unwrap();
      showToast(
        _t("password_reset_successfully", "Password reset successfully!"),
        "success"
      );
      navigate("/login");
    } catch (error) {
      console.error("Password reset error:", error);
      showToast(
        error?.data?.message || "Unable to reset your password. Please check the reset link and try again.",
        "error"
      );
    }
  };

  return (
    <Row className="m-0">
      <Col lg={6} className="p-0">
        <div className="login_wrapper d-flex justify-content-center align-items-center min-vh-100">
          <div className="login_form">
            <img src={LogoImage} alt="logo" className="img-fluid" />
            <div className="d-flex align-items-center w-full justify-content-between">
              <h1>{_t("create_your_password", "Create Your Password")}</h1>
              <LanguageToggleSwitch />
            </div>
            <p className="pt-1">
              {_t("create_a_new_password", "Create a new password")}
            </p>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3 cmn_input">
                <Form.Label>New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder={_t("enter_new_password", "Enter new password")}
                    autoComplete="new-password"
                    value={password}
                    onChange={handlePasswordChange}
                  />
                  <span
                    className="view_pass"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img
                      src={showPassword ? ViewPassword : HidePassword}
                      alt="eye"
                    />
                  </span>
                </div>
              </Form.Group>

              <Form.Group className="mb-4 cmn_input">
                <Form.Label>
                  {_t("confirm_password", "Confirm Password")}
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={_t("confirm_password", "Confirm password")}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                  />
                  <span
                    className="view_pass"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <img
                      src={showConfirmPassword ? ViewPassword : HidePassword}
                      alt="eye"
                    />
                  </span>
                </div>
              </Form.Group>

              <Button
                label={_t("confirm", "Confirm")}
                size="medium"
                className="w-100"
                type="submit"
                isLoading={isLoading}
              />
            </Form>
          </div>
        </div>
      </Col>
      <Col lg={6} className="p-0">
        <div className="info_wrapper min-vh-100 d-flex justify-content-center align-items-center overflow-hidden position-relative">
          <LoginInfo />
        </div>
      </Col>
    </Row>
  );
};

export default ResetPassword;
